export interface SeoMetadata {
  title: string;
  description: string;
  canonical: string;
  robots: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: string;
}

function ensureMetaTag(name: string, content: string, useProperty = false): void {
  const selector = useProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let meta = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!meta) {
    meta = document.createElement('meta');
    if (useProperty) {
      meta.setAttribute('property', name);
    } else {
      meta.setAttribute('name', name);
    }
    document.head.appendChild(meta);
  }

  meta.setAttribute('content', content);
}

function ensureCanonical(url: string): void {
  let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', url);
}

export function applySeo(metadata: SeoMetadata): void {
  document.title = metadata.title;

  ensureMetaTag('description', metadata.description);
  ensureMetaTag('robots', metadata.robots);
  ensureCanonical(metadata.canonical);

  ensureMetaTag('og:type', metadata.ogType || 'website', true);
  ensureMetaTag('og:title', metadata.ogTitle || metadata.title, true);
  ensureMetaTag('og:description', metadata.ogDescription || metadata.description, true);
  ensureMetaTag('og:url', metadata.canonical, true);

  if (metadata.ogImage) {
    ensureMetaTag('og:image', metadata.ogImage, true);
  }

  ensureMetaTag('twitter:card', metadata.twitterCard || 'summary_large_image');
  ensureMetaTag('twitter:title', metadata.ogTitle || metadata.title);
  ensureMetaTag('twitter:description', metadata.ogDescription || metadata.description);

  if (metadata.ogImage) {
    ensureMetaTag('twitter:image', metadata.ogImage);
  }
}

export function setJsonLd(id: string, data: Record<string, unknown>): void {
  clearJsonLd(id);

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

export function clearJsonLd(id: string): void {
  const existing = document.getElementById(id);
  if (existing && existing.parentElement) {
    existing.parentElement.removeChild(existing);
  }
}
